import { createTheme, rem, type CSSVariablesResolver, type MantineColorsTuple } from '@mantine/core';

/**
 * Новый вид: тёплая светло-серая тема дизайнера (handoff 04.11.2026, правило 295).
 *
 * Тема из архива дизайнера перенесена как есть, кроме серых подписей: там
 * `#9B9893`, контраст 2,5:1, и на планшете при дневном свете они не читаются.
 * Дизайнер отдал этот вопрос нам и сам советовал `#6F6C67` (4,6:1); владелец
 * уже жаловался на бледные подписи («дедушка, изучающий телефон», правило 267).
 */
const ink: MantineColorsTuple = ['#F1F0EE', '#EEECE9', '#DEDBD7', '#C9C5BF', '#9B9893', '#6F6C67', '#3B3A38', '#232323', '#1C1C1C', '#121110'];
const gray: MantineColorsTuple = ['#F1F0EE', '#E9E7E4', '#DEDBD7', '#D3D0CB', '#C9C5BF', '#9B9893', '#77736E', '#55524D', '#3B3A38', '#232323'];
const dark: MantineColorsTuple = ['#EEECE9', '#CFCCC7', '#9B9893', '#75726D', '#383633', '#34322F', '#292826', '#1E1D1C', '#232220', '#121110'];
// Единственный акцент: прибыль и срочное
const orange: MantineColorsTuple = ['#FDF1E7', '#FBE0CB', '#F8CBA6', '#F5B27E', '#F39A58', '#F18A40', '#F07A2A', '#C85E17', '#A34B11', '#7E3A0D'];
// Зелёный в дизайне — только значок роста; у нас он ещё и «сходится», «готово»:
// приглушённый, чтобы смысл не пропал, когда цвет был единственным признаком
const bdGreen: MantineColorsTuple = ['#E8F6EF', '#CDEBDD', '#A6DCC3', '#7CCCA7', '#57C796', '#3DAF7E', '#2E9E6B', '#267F56', '#1E6443', '#164A32'];

const FONT = 'Inter, Manrope, "Segoe UI", Roboto, Arial, sans-serif';
export const WARM_MUTED = '#6F6C67';

export const warmTheme = createTheme({
  primaryColor: 'blagodar',
  primaryShade: { light: 7, dark: 1 },
  autoContrast: true,
  luminanceThreshold: 0.45,
  black: '#1C1C1C',
  white: '#FFFFFF',
  colors: {
    blagodar: ink,
    gray,
    dark,
    orange,
    bdGreen,
    blue: ink,
    indigo: ink,
    cyan: ink,
    violet: ink,
    grape: ink,
    pink: ink,
    lime: ink,
    // «Сходится», «принято», «проведён»: на сайте это слово рядом с цветом, но
    // в списках зелёный — единственный быстрый признак; держим его приглушённым
    green: bdGreen,
    teal: bdGreen,
    red: orange,
    yellow: orange,
  },
  fontFamily: FONT,
  headings: {
    fontFamily: FONT,
    fontWeight: '600',
    sizes: {
      h1: { fontSize: rem(30), lineHeight: '1.15' },
      h2: { fontSize: rem(22), lineHeight: '1.2' },
      h3: { fontSize: rem(18), lineHeight: '1.25' },
      h4: { fontSize: rem(16), lineHeight: '1.3' },
    },
  },
  fontSizes: { xs: rem(13), sm: rem(14), md: rem(15), lg: rem(17), xl: rem(20) },
  defaultRadius: 'md',
  radius: { xs: rem(8), sm: rem(12), md: rem(20), lg: rem(30), xl: rem(999) },
  shadows: { xs: 'var(--bd-sh-sm)', sm: 'var(--bd-sh-sm)', md: 'var(--bd-sh)', lg: 'var(--bd-sh)', xl: 'var(--bd-sh-sheet)' },
  components: {
    Button: { defaultProps: { size: 'md', radius: 'xl' } },
    ActionIcon: { defaultProps: { radius: 'xl' } },
    TextInput: { defaultProps: { size: 'md' } },
    NumberInput: { defaultProps: { size: 'md' } },
    Select: { defaultProps: { size: 'md' } },
    Textarea: { defaultProps: { size: 'md' } },
    Badge: { defaultProps: { radius: 'xl', variant: 'light' } },
    Paper: { defaultProps: { radius: 'md' } },
    Card: { defaultProps: { radius: 'md' } },
    SegmentedControl: { defaultProps: { radius: 'xl' } },
    Modal: { defaultProps: { radius: 'lg', overlayProps: { backgroundOpacity: 1, color: 'var(--bd-overlay)' } } },
    Drawer: { defaultProps: { overlayProps: { backgroundOpacity: 1, color: 'var(--bd-overlay)' } } },
    Menu: { defaultProps: { radius: 'md', shadow: 'md' } },
    Popover: { defaultProps: { radius: 'lg', shadow: 'xl' } },
    Notification: { defaultProps: { radius: 'md' } },
  },
});

/*
 * Нейтральные цвета (синий и прочие, что уже стоят в коде экранов) в «лёгком»
 * варианте — белая плашка с тёмным текстом, как метки дизайнера; иначе Mantine
 * подмешивает к фону полупрозрачный тёмный, и метка выходит грязно-серой.
 * Оранжевый (срочное) и зелёный (сходится) остаются своими.
 */
const NEUTRAL = ['blagodar', 'blue', 'indigo', 'cyan', 'violet', 'grape', 'pink', 'lime', 'gray', 'dark'];
const neutralLight = (bg: string, hover: string, ink: string) =>
  Object.fromEntries(
    NEUTRAL.flatMap((c) => [
      [`--mantine-color-${c}-light`, bg],
      [`--mantine-color-${c}-light-hover`, hover],
      [`--mantine-color-${c}-light-color`, ink],
    ]),
  );

export const warmCssVariables: CSSVariablesResolver = () => ({
  variables: {},
  light: {
    ...neutralLight('#FFFFFF', '#F7F6F4', '#1C1C1C'),
    '--mantine-color-body': '#E7E5E2',
    '--mantine-color-text': '#1C1C1C',
    '--mantine-color-dimmed': WARM_MUTED,
    '--mantine-color-default': '#F1F0EE',
    '--mantine-color-default-color': '#1C1C1C',
    '--mantine-color-default-hover': '#E9E7E4',
    '--mantine-color-default-border': '#DEDBD7',
    '--mantine-color-error': '#C85E17',
    '--mantine-color-placeholder': '#9B9893',
    '--mantine-color-anchor': '#1C1C1C',
  },
  dark: {
    ...neutralLight('#34322F', '#3B3936', '#EEECE9'),
    '--mantine-color-body': '#1E1D1C',
    '--mantine-color-text': '#EEECE9',
    '--mantine-color-dimmed': '#A9A6A1',
    '--mantine-color-default': '#292826',
    '--mantine-color-default-color': '#EEECE9',
    '--mantine-color-default-hover': '#34322F',
    '--mantine-color-default-border': '#383633',
    '--mantine-color-error': '#F49655',
    '--mantine-color-placeholder': '#9B9893',
    '--mantine-color-anchor': '#EEECE9',
  },
});
